import React from 'react';
import { styled } from '@linaria/react';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';

type Props = {
  distance: number;
  animateTransitions: boolean;
  axis: 'horizontal' | 'vertical';
};

function Arrow({
  distance,
  animateTransitions,
  axis,
  ...delegated
}: Props) {
  const Component = axis === 'horizontal' ? LeftArrow : TopArrow;

  return (
    <Component
      distance={distance}
      animateTransitions={animateTransitions}
      {...delegated}
    />
  );
}

interface LeftRightArrowProps
  extends React.HTMLAttributes<HTMLDivElement> {
  distance: number;
  animateTransitions: boolean;
}

function LeftArrow({
  distance,
  animateTransitions,
  ...delegated
}: LeftRightArrowProps) {
  return (
    <Wrapper
      as={animateTransitions ? motion.div : 'div'}
      style={{
        width: animateTransitions ? undefined : distance,
        height: 20,
      }}
      // `animate` only exists on motion.div
      // @ts-ignore
      animate={{
        width: animateTransitions ? distance : undefined,
      }}
      {...delegated}
    >
      <ArrowWrapper>
        <HorizontalArrowElem>
          <Left size={20} />
          <Right size={20} />
          <HorizontalShaft />
        </HorizontalArrowElem>
      </ArrowWrapper>
    </Wrapper>
  );
}

function TopArrow({
  distance,
  animateTransitions,
  ...delegated
}: LeftRightArrowProps) {
  return (
    <Wrapper
      as={animateTransitions ? motion.div : 'div'}
      style={{
        width: 20,
        height: animateTransitions ? undefined : distance,
      }}
      // `animate` only exists on motion.div
      // @ts-ignore
      animate={{
        height: animateTransitions ? distance : undefined,
      }}
      {...delegated}
    >
      <ArrowWrapper>
        <VerticalArrowElem>
          <Up size={20} />
          <Down size={20} />
          <VerticalShaft />
        </VerticalArrowElem>
      </ArrowWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

const ArrowWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ArrowElem = styled.div`
  width: 100%;
  height: 100%;
  color: white;
  transform-origin: bottom center;
  backface-visibility: hidden;
  will-change: transform;
`;

const VerticalArrowElem = styled(ArrowElem)`
  animation: verticalEnter 250ms;

  @keyframes verticalEnter {
    from {
      transform: translateY(25%) scaleY(0.5);
      opacity: 0;
    }
    to {
      transform: translateY(0px) scaleY(1);
      opacity: 1;
    }
  }
`;
const HorizontalArrowElem = styled(ArrowElem)`
  animation: horizontalEnter 250ms;

  @keyframes horizontalEnter {
    from {
      transform: translateX(25%) scaleX(0.5);
      opacity: 0;
    }
    to {
      transform: translateX(0px) scaleX(1);
      opacity: 1;
    }
  }
`;

const Up = styled(ChevronUp)`
  position: absolute;
  top: -6px;
`;
const Down = styled(ChevronDown)`
  position: absolute;
  bottom: -6px;
`;
const Left = styled(ChevronLeft)`
  position: absolute;
  left: -6px;
`;
const Right = styled(ChevronRight)`
  position: absolute;
  right: -6px;
`;

const Shaft = styled.div`
  position: absolute;
  margin: auto;
  background: white;
  border-radius: 10px;
`;

const VerticalShaft = styled(Shaft)`
  top: 2px;
  bottom: 2px;
  left: 0;
  right: 0;
  width: 2px;
`;
const HorizontalShaft = styled(Shaft)`
  top: 0;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 2px;
`;

export default Arrow;
